import { EntityCard } from '../shared/EntityCard';
import { Badge } from '../ui/Badge';
import { Clock } from 'lucide-react';
import { ListSchedule } from '@/types/calendar';
import { List } from '@/types/lists';
import { cn } from '@/utils/cn';

interface ScheduleCardProps {
  schedule: ListSchedule;
  list?: List;
  onEdit?: (schedule: ListSchedule) => void;
  onDelete?: (schedule: ListSchedule) => void;
  onToggleActive?: (schedule: ListSchedule) => void;
  onClick?: (schedule: ListSchedule) => void;
  className?: string;
}

function formatScheduleType(schedule: ListSchedule): string {
  switch (schedule.type) {
    case 'one_time':
      return schedule.scheduledDate
        ? new Date(schedule.scheduledDate).toLocaleDateString()
        : 'One-time';
    case 'daily': {
      const dailyDays = schedule.recurringPattern?.daysOfWeek?.length
        ? `on ${schedule.recurringPattern.daysOfWeek.join(', ')}`
        : '';
      return `Daily ${dailyDays} at ${schedule.recurringPattern?.time || ''}`.trim();
    }
    case 'weekly': {
      const days = schedule.recurringPattern?.daysOfWeek?.join(', ') || '';
      return `Weekly on ${days} at ${schedule.recurringPattern?.time}`;
    }
    case 'monthly':
      return `Monthly on day ${schedule.recurringPattern?.dayOfMonth} at ${schedule.recurringPattern?.time}`;
    default:
      return schedule.type;
  }
}

export function ScheduleCard({
  schedule,
  list,
  onEdit,
  onDelete,
  onToggleActive,
  onClick,
  className
}: ScheduleCardProps) {
  return (
    <EntityCard
      testId="schedule-card"
      title={schedule.title || list?.title || 'Untitled Schedule'}
      description={schedule.description}
      status={schedule.isActive ? 'active' : 'paused'}
      statusLabel={schedule.isActive ? 'Active' : 'Paused'}
      progress={0}
      showProgress={false}
      isActive={schedule.isActive}
      className={cn('schedule-card', className)}
      onEdit={onEdit ? () => onEdit(schedule) : undefined}
      onDelete={onDelete ? () => onDelete(schedule) : undefined}
      onToggleActive={onToggleActive ? () => onToggleActive(schedule) : undefined}
      onClick={onClick ? () => onClick(schedule) : undefined}
      badges={
        <Badge variant="outline">
          {schedule.type.replace('_', ' ')}
        </Badge>
      }
      metadata={
        <>
          <div className="entity-card-meta">
            <Clock className="h-4 w-4" />
            <span>{formatScheduleType(schedule)}</span>
          </div>

          {list?.title && schedule.title !== list.title && (
            <div className="entity-card-meta">
              <span>List: {list.title}</span>
            </div>
          )}

          {schedule.notifications && schedule.notifications.length > 0 && (
            <div className="entity-card-meta">
              <span>
                {schedule.notifications.length} notification{schedule.notifications.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </>
      }
    />
  );
}
